import { Component, OnInit, signal, HostListener, inject, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { BookingModal } from '../../components/booking-modal/booking-modal';
import { MetaService } from '../../services/meta.service';
import { SiteSettingsService } from '../../services/site-settings.service';

interface Service {
	id: number;
	service_name: string;
	slug: string;
	tagline: string;
	description: string;
	short_description: string;
	pricing: number;
	duration_minutes: number;
	duration_formatted: string;
	image_path: string;
	category: ServiceCategory;
}

interface ServiceCategory {
	id: number;
	name: string;
	slug: string;
	description: string;
	icon: string;
	image: string;
	services: Service[];
}

@Component({
	selector: 'app-services',
	standalone: true,
	imports: [CommonModule, RouterLink, BookingModal],
	templateUrl: './services.html',
	styleUrl: './services.css'
})
export class ServicesPage implements OnInit {
	categories = signal<ServiceCategory[]>([]);
	isLoading = signal(true);
	activeCategory = signal<ServiceCategory | null>(null);
	hoveredService = signal<Service | null>(null);
	storageUrl = environment.storageUrl;
	isScrolled = false;
	showBookingModal = signal(false);

	// Scroll state for services
	canScrollLeft = signal(false);
	canScrollRight = signal(false);
	@ViewChild('servicesScrollContainer') servicesScrollContainer!: ElementRef<HTMLDivElement>;

	private settings = inject(SiteSettingsService);

	// Site Settings — already loaded via APP_INITIALIZER
	heroTagline = signal(this.settings.val('services_hero', 'services_hero_tagline', 'Premium Treatments'));
	heroTitle = signal(this.settings.val('services_hero', 'services_hero_title', 'Our Services'));
	heroDescription = signal(this.settings.val('services_hero', 'services_hero_description', 'Discover our world-class hair restoration treatments, crafted with precision and care to deliver exceptional results.'));
	heroPhone = signal(this.settings.val('contact', 'contact_phone', '+91 98765 43210'));

	categoriesTagline = signal(this.settings.val('services_categories', 'services_categories_tagline', 'Our Expertise'));
	categoriesTitle = signal(this.settings.val('services_categories', 'services_categories_title', 'Choose Your Treatment'));
	categoriesDescription = signal(this.settings.val('services_categories', 'services_categories_description', 'Select a category to explore our comprehensive range of premium services'));

	whyChooseTagline = signal(this.settings.val('services_why_choose', 'services_why_choose_tagline', 'Why Choose Us'));
	whyChooseTitleLine1 = signal(this.settings.val('services_why_choose', 'services_why_choose_title_line1', 'Experience the'));
	whyChooseTitleLine2 = signal(this.settings.val('services_why_choose', 'services_why_choose_title_line2', 'Difference'));
	whyChooseDescription = signal(this.settings.val('services_why_choose', 'services_why_choose_description', 'Our commitment to excellence sets us apart. With cutting-edge technology, expert specialists, and personalized care, we deliver transformative results that exceed expectations.'));
	whyChooseFeature1Title = signal(this.settings.val('services_why_choose', 'services_why_choose_feature1_title', 'Certified Experts'));
	whyChooseFeature1Desc = signal(this.settings.val('services_why_choose', 'services_why_choose_feature1_description', 'Board-certified specialists with years of experience'));
	whyChooseFeature2Title = signal(this.settings.val('services_why_choose', 'services_why_choose_feature2_title', 'Advanced Technology'));
	whyChooseFeature2Desc = signal(this.settings.val('services_why_choose', 'services_why_choose_feature2_description', 'State-of-the-art equipment for optimal results'));
	whyChooseFeature3Title = signal(this.settings.val('services_why_choose', 'services_why_choose_feature3_title', 'Personalized Care'));
	whyChooseFeature3Desc = signal(this.settings.val('services_why_choose', 'services_why_choose_feature3_description', 'Customized treatment plans for your unique needs'));
	whyChooseStat1Value = signal(this.settings.val('services_why_choose', 'services_why_choose_stat1_value', '10K+'));
	whyChooseStat1Label = signal(this.settings.val('services_why_choose', 'services_why_choose_stat1_label', 'Happy Clients'));
	whyChooseStat2Value = signal(this.settings.val('services_why_choose', 'services_why_choose_stat2_value', '15+'));
	whyChooseStat2Label = signal(this.settings.val('services_why_choose', 'services_why_choose_stat2_label', 'Years Experience'));
	whyChooseStat3Value = signal(this.settings.val('services_why_choose', 'services_why_choose_stat3_value', '98%'));
	whyChooseStat3Label = signal(this.settings.val('services_why_choose', 'services_why_choose_stat3_label', 'Success Rate'));
	whyChooseStat4Value = signal(this.settings.val('services_why_choose', 'services_why_choose_stat4_value', '24/7'));
	whyChooseStat4Label = signal(this.settings.val('services_why_choose', 'services_why_choose_stat4_label', 'Support Available'));

	constructor(
		private http: HttpClient,
		private route: ActivatedRoute,
		private router: Router
	) {}

	private metaService = inject(MetaService);

	ngOnInit(): void {
		this.metaService.setPageMeta('services');
		this.loadCategories();
	}

	@HostListener('window:scroll', [])
	onWindowScroll() {
		this.isScrolled = window.scrollY > 100;
	}

	loadCategories(): void {
		this.http.get<any>(`${environment.apiUrl}/public/service-categories`).subscribe({
			next: (response) => {
				if (response.status && response.data) {
					const cats = response.data.filter((cat: ServiceCategory) =>
						cat.services && cat.services.length > 0
					);
					this.categories.set(cats);

					// Check for category query param
					this.route.queryParams.subscribe(params => {
						if (params['category']) {
							const found = cats.find((c: ServiceCategory) => c.slug === params['category']);
							if (found) {
								this.activeCategory.set(found);
								setTimeout(() => this.scrollToServices(), 300);
							} else if (cats.length > 0) {
								this.activeCategory.set(cats[0]);
							}
						} else if (cats.length > 0) {
							this.activeCategory.set(cats[0]);
						}
					});
				}
				this.isLoading.set(false);
				setTimeout(() => this.updateScrollState(), 300);
			},
			error: () => {
				this.isLoading.set(false);
			}
		});
	}

	selectCategory(category: ServiceCategory): void {
		this.activeCategory.set(category);
		this.scrollToServices();
		// Update scroll state after category change
		setTimeout(() => this.updateScrollState(), 100);
	}

	scrollToServices(): void {
		const element = document.getElementById('services-section');
		if (element) {
			element.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}

	getImageUrl(imagePath: string | null): string {
		if (!imagePath) {
			return '/images/service-placeholder.jpg';
		}
		if (imagePath.startsWith('http')) {
			return imagePath;
		}
		return `${this.storageUrl}/${imagePath}`;
	}

	getCategoryImage(category: ServiceCategory): string {
		if (category.image) {
			return `${this.storageUrl}/${category.image}`;
		}
		return '/images/category-placeholder.jpg';
	}

	setHoveredService(service: Service | null): void {
		this.hoveredService.set(service);
	}

	/**
	 * Services now have a full detail page of their own, so a click navigates
	 * there rather than opening the old summary modal.
	 */
	openServiceDetail(service: Service): void {
		this.router.navigate(['/services', service.slug]);
	}

	openBookingModal(): void {
		this.showBookingModal.set(true);
	}

	closeBookingModal(): void {
		this.showBookingModal.set(false);
	}

	// Horizontal scroll methods
	scrollServices(direction: 'left' | 'right'): void {
		const container = this.servicesScrollContainer?.nativeElement;
		if (!container) return;
		const scrollAmount = 440;
		container.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
	}

	onServicesScroll(): void {
		this.updateScrollState();
	}

	private updateScrollState(): void {
		const container = this.servicesScrollContainer?.nativeElement;
		if (!container) {
			this.canScrollLeft.set(false);
			this.canScrollRight.set(false);
			return;
		}
		this.canScrollLeft.set(container.scrollLeft > 10);
		this.canScrollRight.set(container.scrollLeft < container.scrollWidth - container.clientWidth - 10);
	}
}
